import { useCallback, useEffect, useRef, useState } from "react";
import { App, Flex, Select } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { records as fetchEquipmentRecords } from "@/apis/equipment";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import useDataList from "@/hooks/useDataList";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { EquipmentRecord } from "@/types/equipment";
import EquipmentRecordDetail from "../EquipmentRecordDetail";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";
import useEquipmentRecordColumn from "../../hooks/useEquipmentRecordColumn";

/** 设备记录列表组件入参。 */
interface EquipmentRecordsProps {
  equipmentId: string;
}

/** 设备记录列表的查询条件。 */
interface EquipmentRecordFilters {
  recordType?: number;
}

/** 设备记录列表的默认查询条件。 */
const INITIAL_RECORD_FILTERS: EquipmentRecordFilters = {
  recordType: undefined,
};

/** 展示指定设备的操作记录，并支持查看详情。 */
const EquipmentRecords = ({ equipmentId }: EquipmentRecordsProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  // 保存表格分页状态，以便筛选时回到首页。
  const tableRef = useRef<TableLayoutRef | null>(null);
  // 记录当前列表请求，切换设备或筛选条件时取消旧请求。
  const listControllerRef = useRef<AbortController>();
  const [items, , loading, { fetchList, clearData }] = useDataList<
    EquipmentRecord,
    Parameters<typeof fetchEquipmentRecords>
  >({ fetchApi: fetchEquipmentRecords });
  const [detailRecordId, setDetailRecordId] = useState<string>();
  const { recordTypeItems: typeItems } = useEquipmentEnumsContext();
  const {
    draftFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<EquipmentRecordFilters>(INITIAL_RECORD_FILTERS);
  const { recordType } = draftFilters;

  // 加载设备记录，并阻止已取消请求覆盖最新结果。
  const loadRecords = useCallback(async (nextRecordType?: number) => {
    listControllerRef.current?.abort();
    const controller = new AbortController();
    listControllerRef.current = controller;

    try {
      await fetchList(
        equipmentId,
        nextRecordType ?? null,
        controller,
      );
    } catch (error) {
      if (!controller.signal.aborted) {
        notification.error({
          message: t("equipment.recordListFetchFailed"),
          description: errorCodes[Number(error)] ?? String(error),
        });
      }
    }
  }, [equipmentId, fetchList, notification, t]);

  // 切换设备时重置筛选、分页和已打开的详情。
  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_RECORD_FILTERS);
    clearData();
    setDetailRecordId(undefined);
    tableRef.current?.resetTablePage();
    loadRecords(nextFilters.recordType);

    return () => listControllerRef.current?.abort();
  }, [clearData, equipmentId, loadRecords, replaceFilters]);

  const handleDetail = useCallback((id: string) => {
    setDetailRecordId(id);
  }, []);

  const columns = useEquipmentRecordColumn({
    recordTypeItems: typeItems,
    onDetail: handleDetail,
  });

  // 详情接口缺少部分关联数据，打开详情时保留完整列表记录作为补充数据源。
  const detailListRecord = detailRecordId
    ? items.find(item => item.id === detailRecordId)
    : undefined;

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    loadRecords(nextFilters.recordType);
  };

  const handleReset = () => {
    const nextFilters = replaceFilters(INITIAL_RECORD_FILTERS);
    tableRef.current?.resetTablePage();
    loadRecords(nextFilters.recordType);
  };

  const headerChildren = (
    <Flex justify="flex-end" style={{ width: "100%" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleReset}
          disabled={loading}
        >
          <Select
            value={recordType}
            allowClear
            placeholder={t("equipment.recordType")}
            options={typeItems.map(({ v, desc }) => ({
              value: v,
              label: desc,
            }))}
            style={{ width: 180 }}
            onChange={value => updateDraftFilters({ recordType: value })}
          />
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex
      vertical
      style={{ width: "100%", flex: 1, minWidth: 0, minHeight: 0 }}
    >
      <STableLayout
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={items.length}
        scrollX={1454}
      />
      {detailRecordId && detailListRecord && (
        <EquipmentRecordDetail
          open={Boolean(detailRecordId)}
          listRecord={detailListRecord}
          recordTypeItems={typeItems}
          onCancel={() => setDetailRecordId(undefined)}
        />
      )}
    </Flex>
  );
};

export default EquipmentRecords;
